// File: packages/calculator/components/PricingCalculator.tsx

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import type { CalculatorSpec, ShareableCalculatorState, QuotePayload } from '@dopomogai/types';
import { CalculatorProvider } from '@dopomogai/agent-kit';

import { PlatformSelector } from './PlatformSelector';
import { BundleSelector } from './BundleSelector';
import { QuickPicks } from './QuickPicks';
import { FeatureGroup } from './FeatureGroup';
import { ROISection } from './ROISection';
import { SummaryPanel } from './SummaryPanel';
import { Disclaimers } from './Disclaimers';

type Props = {
  /** The full specification object that drives the calculator's content and logic. */
  spec: CalculatorSpec;
  /** An optional state object to pre-configure the calculator (e.g., from a shared URL). */
  prefill?: ShareableCalculatorState;
  /** Callback fired when the user clicks the "Get Quote" button. */
  onGetQuote?: (payload: QuotePayload) => void;
  /** Callback fired when the user clicks the "Book a Call" button. */
  onBookCall?: () => void;
};

/**
 * The main layout component for the pricing calculator.
 * It composes all the necessary sub-components and provides them with
 * the calculator state via the CalculatorProvider.
 */
export function PricingCalculator({ spec, prefill, onGetQuote, onBookCall }: Props) {
  const [hideAdvanced, setHideAdvanced] = useState<boolean>(true);

  const anyAdvanced = useMemo(
    () => spec.groups.some((g) => g.features.some((f) => f.visibility === 'advanced')),
    [spec.groups]
  );

  const advancedCount = useMemo(
    () =>
      spec.groups.reduce((acc, g) => acc + g.features.filter((f) => f.visibility === 'advanced').length, 0),
    [spec.groups]
  );

  return (
    <CalculatorProvider spec={spec} prefill={prefill}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* === Left Column: Configuration === */}
        <div className="lg:col-span-2 space-y-8">
          <header>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">{spec.title}</h2>
          </header>

          <PlatformSelector />
          <BundleSelector />
          <QuickPicks />

          {anyAdvanced && (
            <div className="flex items-center justify-between pt-4 border-t-2 border-slate-200">
              <div className="text-sm font-semibold text-slate-800">Fine-tune your plan</div>
              <button
                type="button"
                className="text-xs font-semibold inline-flex items-center gap-1.5 border-2 border-black rounded-lg px-3 py-1 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[2px_2px_0_0_#000]"
                onClick={() => setHideAdvanced((v) => !v)}
                aria-expanded={!hideAdvanced}
                aria-controls="feature-groups-container"
              >
                {hideAdvanced ? (
                  <>Show advanced ({advancedCount}) <ChevronDown className="w-4 h-4" /></>
                ) : (
                  <>Hide advanced <ChevronUp className="w-4 h-4" /></>
                )}
              </button>
            </div>
          )}

          <div id="feature-groups-container" className="space-y-4">
            {spec.groups.map((group) => (
              <FeatureGroup key={group.id} group={group} hideAdvanced={hideAdvanced} />
            ))}
          </div>

          <ROISection />
          <Disclaimers
            socialImpactPct={spec.socialImpactPct}
            disclaimers={spec.disclaimers}
            defaultOpen={false}
          />
        </div>

        {/* === Right Column: Sticky Summary === */}
        <aside className="lg:col-span-1">
          <SummaryPanel onGetQuote={onGetQuote} onBookCall={onBookCall} />
        </aside>
      </div>
    </CalculatorProvider>
  );
}